/** A value field that keeps a literal's JSON type (literals.ts): a template /
    text field plus a small type chip — `abc` text, `123` number, `T/F`
    boolean, `null`, `{…}` template. Clicking the chip of a value that reads
    as a literal switches it between the typed value and the same text. */

import type { TokenGroup } from './context'
import { useWizardDesign } from './context'
import ExprInput from './ExprInput'
import { literalKind, literalText, nextLiteral, toggleLiteral, isForcedText } from './literals'
import { TextInput } from './ui'

const CHIP: Record<string, string> = {
  text: 'abc', template: '{…}', number: '123', boolean: 'T/F', null: 'null', json: 'JSON',
}

const TITLE: Record<string, string> = {
  text: 'Text',
  template: 'A template — {tokens} filled in when the step runs',
  number: 'A number — click to keep it as text instead',
  boolean: 'True / false — click to keep it as text instead',
  null: 'Null (no value) — click to keep it as text instead',
  json: 'A structured value (edit it as JSON in the model)',
}

export function TypeChip({ value, onChange, disabled }: {
  value: unknown
  onChange: (v: unknown) => void
  disabled?: boolean
}) {
  const kind = literalKind(value)
  const forced = isForcedText(value)
  const canToggle = !disabled && (forced || kind === 'number' || kind === 'boolean' || kind === 'null')
  const title = forced ? 'Kept as text — click to store it as a typed value' : TITLE[kind]
  return (
    <button type="button" title={title} aria-label={`Value type: ${forced ? 'text' : kind}`}
      disabled={!canToggle} onClick={() => onChange(toggleLiteral(value))}
      style={{
        flexShrink: 0, minWidth: 34, height: 22, marginTop: 3, padding: '0 5px', fontSize: 13,
        fontFamily: 'monospace', borderRadius: 9, cursor: canToggle ? 'pointer' : 'default',
        border: '1px solid var(--color-border)',
        background: kind === 'text' || kind === 'template' ? 'transparent' : 'var(--color-bg-secondary)',
        color: forced ? 'var(--color-warning, #b45309)'
          : kind === 'text' || kind === 'template' ? 'var(--color-text-secondary)' : 'var(--color-accent)',
      }}>
      {forced ? '"…"' : CHIP[kind]}
    </button>
  )
}

/** A template field (with token chips) whose literals keep their type. */
export default function TypedValueInput({
  value, onChange, placeholder, formNo, extraTokens, disabled, template = true,
}: {
  value: unknown
  /** `undefined` when the field was cleared. */
  onChange: (v: unknown) => void
  placeholder?: string
  formNo?: string
  extraTokens?: TokenGroup[]
  disabled?: boolean
  /** false — a plain text field (no {tokens}), e.g. a fixed choice's value. */
  template?: boolean
}) {
  const ctx = useWizardDesign()
  const locked = disabled || ctx.readOnly
  const set = (text: string) => onChange(text === '' ? '' : nextLiteral(text, value))
  return (
    <div style={{ display: 'flex', gap: 4, alignItems: 'flex-start' }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        {template ? (
          <ExprInput mode="template" value={literalText(value)} onChange={set} placeholder={placeholder}
            formNo={formNo} extraTokens={extraTokens} disabled={disabled} />
        ) : (
          <TextInput value={literalText(value)} onChange={set} placeholder={placeholder} mono disabled={locked} />
        )}
      </div>
      <TypeChip value={value} onChange={onChange} disabled={locked} />
    </div>
  )
}
